/**
 * @file `usePagedList` — page state + viewport-fit page size + first-row
 *       anchoring, bundled so every numbered-pager panel wires pagination
 *       the same way.
 * @module @labcraft/admin/ui/hooks/use-paged-list
 *
 * Composes {@link usePageSize} (the rows that fit the scroll container) with
 * {@link anchorPage} (keep the first visible row on screen when the fit
 * changes — a window resize, or a `LowerSplit` detail pane opening and
 * shrinking the table) and the page-overflow clamp. A control change calls
 * `resetPage()` to jump back to page 1; a page-size change re-anchors instead
 * of resetting, so opening a row never loses the operator's place.
 *
 * `total` is the record count across all pages — `filtered.length` for a
 * client-sliced panel (known before this hook runs), or the server's reported
 * total for an offset-paged one. It drives `pageCount` and the clamp only;
 * `pageSize` and `offset` are independent of it, so an offset-paged caller can
 * feed `offset` into its query and sync the resulting `total` back in on the
 * next render without a chicken-and-egg. Panels slice their own rows with
 * `rows.slice(offset, offset + pageSize)` (client-sliced) or send
 * `limit`/`offset` to their query (offset-paged).
 */

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { anchorPage } from './page_anchor.ts'
import { usePageSize, type UsePageSizeOptions } from './use_page_size.ts'

/**
 * The paged-list state a numbered-pager panel needs. `page`/`pageCount` feed
 * the `<Pager>`, `pageSize`/`offset` drive the slice or query, and `rowHeight`
 * is the scoped `--row-h` override that fills the body area (see
 * {@link usePageSize}).
 *
 * @property page - 1-based current page.
 * @property setPage - Set the current page (e.g. from the `<Pager>`).
 * @property resetPage - Jump to page 1; wire to every filter/control change.
 * @property pageSize - Rows that fit the container (0 before first measure).
 * @property rowHeight - Per-row height in px that fills the body with no gap.
 * @property offset - `(page - 1) * pageSize` — the slice/query start index.
 * @property pageCount - Total pages (≥ 1), derived from `total` and `pageSize`.
 */
export type PagedList = {
  page: number
  setPage: (page: number) => void
  resetPage: () => void
  pageSize: number
  rowHeight: number
  offset: number
  pageCount: number
}

/**
 * Options for {@link usePagedList} — the {@link usePageSize} tuning plus an
 * optional row to keep visible across a page-size change.
 *
 * @property anchorIndex - 0-based absolute index of a row to keep on the
 *   visible page when the page size changes (e.g. the selected row while a
 *   detail pane is open). When set, a size change pages to the page containing
 *   this index; `null`/omitted falls back to first-visible-row anchoring, so a
 *   plain window resize still preserves the operator's place.
 */
export type UsePagedListOptions = UsePageSizeOptions & {
  anchorIndex?: number | null
}

/**
 * Drive a numbered-pager panel from its scroll container and record count.
 *
 * @param ref - The scroll container whose height bounds the page.
 * @param total - Record count across all pages (page count = ceil(total /
 *   pageSize)).
 * @param options - {@link usePageSize} tuning plus `anchorIndex` (keep a
 *   specific row visible across a size change).
 * @returns The {@link PagedList} state for the panel and its `<Pager>`.
 */
export function usePagedList(
  ref: RefObject<HTMLElement | null>,
  total: number,
  options: UsePagedListOptions = {},
): PagedList {
  const { anchorIndex, ...sizeOptions } = options
  const [page, setPage] = useState(1)
  const { pageSize, rowHeight } = usePageSize(ref, sizeOptions)

  const offset = (page - 1) * pageSize
  const pageCount = pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1

  const resetPage = useCallback(() => {
    setPage(1)
  }, [])

  // A page-size change (viewport resize, or the detail pane opening/closing
  // and shrinking the table) re-pages without snapping to page 1: when an
  // `anchorIndex` is set (a selected row + open detail), page to the row so it
  // stays visible and selection-highlighted next to its detail; otherwise
  // anchor the first visible row so a plain resize preserves the operator's
  // place. A change that leaves the page size untouched is a no-op.
  const prevPageSize = useRef(pageSize)
  useEffect(() => {
    const prev = prevPageSize.current
    if (prev === pageSize) return
    prevPageSize.current = pageSize
    setPage(p =>
      anchorIndex != null && anchorIndex >= 0
        ? Math.floor(anchorIndex / pageSize) + 1
        : anchorPage(p, prev, pageSize),
    )
  }, [pageSize, anchorIndex])

  // Clamp if the current page falls past the end after a filter narrows the
  // set (or the record count drops). A functional update composes with the
  // anchor effect above: when a size change fires both in one commit, the
  // anchor sets the new page first, then this trims it to range — instead of a
  // value-set from a stale `page` that would clobber the anchor and jump the
  // list to the last page.
  useEffect(() => {
    setPage(p => (p > pageCount ? pageCount : p))
  }, [pageCount])

  return { page, setPage, resetPage, pageSize, rowHeight, offset, pageCount }
}
